<template>
  <el-dialog
    :title="dialogTitle"
    :visible.sync="visible"
    width="500px"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    :show-close="isComplete"
  >
    <!-- 执行中 -->
    <div v-if="!isComplete">
      <div style="text-align: center; margin-bottom: 20px;">
        <div style="font-size: 16px; margin-bottom: 10px;">
          正在处理：第 {{ currentIndex }} 条 / 共 {{ total }} 条
        </div>
        <el-progress :percentage="progressPercent" :status="progressStatus" />
      </div>

      <div style="display: flex; justify-content: space-around; text-align: center; margin-bottom: 15px;">
        <div>
          <el-tag type="success">成功 {{ successCount }} 条</el-tag>
        </div>
        <div>
          <el-tag type="danger">失败 {{ failCount }} 条</el-tag>
        </div>
      </div>

      <div v-if="currentItem" style="padding: 10px; background-color: #f5f7fa; border-radius: 4px;">
        <div style="font-size: 12px; color: #909399;">当前处理：</div>
        <div style="font-size: 14px; margin-top: 5px;">
          {{ currentItem.feeName }} - {{ currentItem.payerObjName }} - {{ currentItem.receivedAmount }}元
        </div>
      </div>

      <div v-if="!isComplete" style="text-align: center; margin-top: 20px;">
        <el-button size="small" @click="handleCancel">取消</el-button>
      </div>
    </div>

    <!-- 执行完成 -->
    <div v-else>
      <div style="text-align: center; margin-bottom: 20px;">
        <i
          :class="failCount === 0 ? 'el-icon-success' : 'el-icon-warning'"
          :style="{ fontSize: '48px', color: failCount === 0 ? '#67C23A' : '#E6A23C' }"
        />
        <div style="font-size: 18px; margin-top: 10px;">
          {{ failCount === 0 ? '批量申请退费完成' : '批量申请退费完成（部分失败）' }}
        </div>
        <div style="color: #909399; margin-top: 5px;">
          成功 {{ successCount }} 条，失败 {{ failCount }} 条
        </div>
      </div>

      <!-- 失败记录列表 -->
      <div v-if="failList.length > 0">
        <div style="font-size: 14px; font-weight: bold; margin-bottom: 10px;">失败记录：</div>
        <el-table :data="failList" size="mini" border style="width: 100%; max-height: 200px; overflow-y: auto;">
          <el-table-column prop="item.feeName" label="费用名称" align="center" />
          <el-table-column prop="item.payerObjName" label="付费对象" align="center" />
          <el-table-column prop="errorMsg" label="失败原因" align="center" show-overflow-tooltip />
        </el-table>
      </div>
    </div>

    <span v-if="isComplete" slot="footer" class="dialog-footer">
      <el-button @click="handleClose">关闭</el-button>
      <el-button v-if="failList.length > 0" type="primary" @click="handleRetry">重试失败项</el-button>
    </span>
  </el-dialog>
</template>

<script>
import { saveReturnPayFee } from '@/api/fee/propertyFeeApi'
import { getCommunityId } from '@/api/community/communityApi'

export default {
  name: 'BatchReturnPayFeeProgress',

  data() {
    return {
      visible: false,
      items: [],
      reason: '',
      currentIndex: 0,
      total: 0,
      successCount: 0,
      failCount: 0,
      failList: [],
      currentItem: null,
      isComplete: false,
      isCancelled: false
    }
  },

  computed: {
    dialogTitle() {
      return this.isComplete ? '批量申请退费完成' : '正在批量申请退费'
    },
    progressPercent() {
      if (this.total === 0) return 0
      return Math.round((this.currentIndex / this.total) * 100)
    },
    progressStatus() {
      if (this.isCancelled) return 'exception'
      return ''
    }
  },

  methods: {
    open(items, reason) {
      this.items = this.expandItems(items)
      this.reason = reason
      this.currentIndex = 0
      this.total = this.items.length
      this.successCount = 0
      this.failCount = 0
      this.failList = []
      this.currentItem = null
      this.isComplete = false
      this.isCancelled = false
      this.visible = true

      // 延迟开始执行，确保弹窗已显示
      this.$nextTick(() => {
        this.startBatchProcess()
      })
    },

    /**
     * 展开合并记录为独立明细
     * 合并记录可能包含多个 detailId，需要逐条申请退费
     */
    expandItems(items) {
      const expanded = []
      for (const item of items) {
        if (item.detailIds && item.detailIds.includes(',')) {
          const ids = item.detailIds.split(',')
          for (const id of ids) {
            expanded.push({
              ...item,
              detailId: id.trim(),
              _isExpanded: true
            })
          }
        } else {
          expanded.push(item)
        }
      }
      return expanded
    },

    async startBatchProcess() {
      for (let i = 0; i < this.items.length; i++) {
        if (this.isCancelled) break

        this.currentIndex = i + 1
        const item = this.items[i]
        this.currentItem = item

        try {
          await this.applyRefund(item)
          this.successCount++
        } catch (error) {
          this.failCount++
          this.failList.push({
            item,
            errorMsg: error.message || '申请退费失败'
          })
        }

        // 给 UI 刷新时间
        await this.sleep(100)
      }

      this.currentItem = null
      this.isComplete = true
    },

    async applyRefund(item) {
      const params = {
        detailId: item.detailId,
        cycles: item.cycles,
        receivableAmount: item.receivableAmount,
        receivedAmount: item.receivedAmount,
        reason: this.reason,
        communityId: getCommunityId(),
        feeId: item.feeId,
        feeTypeCd: item.feeTypeCd,
        configId: item.configId,
        payTime: item.createTime
      }

      await saveReturnPayFee(params)
    },

    sleep(ms) {
      return new Promise(resolve => setTimeout(resolve, ms))
    },

    handleCancel() {
      this.isCancelled = true
      this.$message.info('已取消，正在处理中的记录将继续完成')
    },

    handleClose() {
      this.visible = false
      this.$emit('complete', {
        successCount: this.successCount,
        failCount: this.failCount,
        failList: this.failList
      })
    },

    handleRetry() {
      const retryItems = this.failList.map(f => f.item)
      this.failList = []
      this.failCount = 0
      this.successCount = 0
      this.currentIndex = 0
      this.isComplete = false
      this.isCancelled = false
      this.items = retryItems
      this.total = retryItems.length
      this.startBatchProcess()
    }
  }
}
</script>
